export class ResponseError extends Error {
  public response: Response;
  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return { success: true, status: response.status };
  }
  // The content of the body lenght has to be greater than 0. Otherwise response.json will throw an error.
  let contentLength = response.headers.get('content-length');
  if (contentLength !== '0') return response.json();

  // Return a success object for empty responses
  return { success: true, status: response.status };
}

/**
 * Parses the Text returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {string}          The parsed Text from the request
 */
function parseText(response: Response) {
  if (response.status === 204 || response.status === 205) {
    return `Success: ${response.status}`;
  }
  // The content of the body lenght has to be greater than 0. Otherwise response.json will throw an error.
  let contentLength = response.headers.get('content-length');
  if (contentLength !== '0') return response.text();

  // Return a success message for empty responses
  return `Success: ${response.status}`;
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error = new ResponseError(response);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(
  url: string,
  options?: RequestInit,
  returnError?: boolean,
): Promise<{} | null | { err: ResponseError }> {
  try {
    const fetchResponse = await fetch(url, options);
    const response = checkStatus(fetchResponse);
    return parseJSON(response);
  } catch (err) {
    console.error(err);
    if (returnError && err instanceof ResponseError) {
      return { err };
    }
    return null;
  }
}

export async function requestText(
  url: string,
  options?: RequestInit,
): Promise<string | null | undefined | { err: ResponseError }> {
  try {
    const fetchResponse = await fetch(url, options);
    const response = checkStatus(fetchResponse);
    return parseText(response);
  } catch (err) {
    console.error(err);
    if (err instanceof ResponseError) {
      return { err };
    }
    return null;
  }
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response wiil be of { isSuccess: boolean } e.g true for success result.
 */

export async function requestStatus(
  url: string,
  options?: RequestInit,
): Promise<{
  isSuccess: boolean;
  status?: number;
  err?: ResponseError;
}> {
  try {
    const response = await fetch(url, options);
    if (response.status >= 200 && response.status < 300) {
      return { isSuccess: true, status: response.status };
    } else {
      const error = new ResponseError(response);
      return { isSuccess: false, status: response.status, err: error };
    }
  } catch (err) {
    console.error(err);
    if (err instanceof ResponseError) {
      return { isSuccess: false, status: err.response.status, err };
    }
    return { isSuccess: false };
  }
}

// export async function callApi(
//   method: string,
//   url: string,
//   path: string,
//   data?: any,
// ) {
//   debugger;
//   const response = await fetch(`${url}/api${path}`, {
//     method,
//     headers: {
//       'Content-Type': 'application/json',
//       Accept: 'application/json',
//     },
//     body: JSON.stringify(data),
//   });
//   //debugger;
//   if (response.ok) return response.json();
//   if (response.status === 400) {
//     const error = await response.text();
//     throw new Error(error);
//   }

//   throw new Error('Network response was not ok');
// }
